import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, Directive, inject, input, model } from '@angular/core';
import { ClassValue } from 'clsx';
import { bui, UniqueId } from '../utils';

@Component({
  selector: 'collapsable',
  exportAs: 'collapsable',
  host: {
    '[class]': 'computedClass()',
    '[attr.data-expanded]': 'expanded() ? true : null',
  },
  template: `<ng-content />`,
})
export class Collapsable {
  // Dependencies
  private readonly uniqueId = inject(UniqueId);

  // Inputs / Outputs
  readonly expanded = model(false);
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  readonly id = this.uniqueId.getId('bui-collapsable-', true);
  readonly triggerId = this.uniqueId.getId('bui-collapsable-trigger-', true);
  readonly contentId = this.uniqueId.getId('bui-collapsable-content-', true);
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsable',
      'flex flex-col',

      // User classes
      this.userClass(),
    ),
  );

  /**
   * Toggle the collapsed state.
   * @param force Optional boolean to explicitly set the collapsed state.
   */
  toggle(force?: boolean) {
    if (force === true || force === false) {
      this.expanded.set(force);
      return;
    }

    this.expanded.update((value) => !value);
  }
}

@Directive({
  selector: '[collapsableTrigger]',
  exportAs: 'collapsableTriggerBehavior',
  host: {
    role: 'button',
    tabindex: '0',
    '[id]': 'triggerId',
    '[class]': 'computedClass()',
    '[attr.aria-controls]': 'contentId',
    '[attr.aria-expanded]': 'expanded()',
    '(pointerdown)': 'toggle()',
    '(keydown.space)': 'toggle()',
    '(keydown.enter)': 'toggle()',
  },
})
export class CollapsableTriggerBehavior {
  // Dependencies
  private readonly collapsable = inject(Collapsable);

  // Inputs / Outputs
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  readonly expanded = computed(() => this.collapsable.expanded());
  protected readonly triggerId = this.collapsable.triggerId;
  protected readonly contentId = this.collapsable.contentId;
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsible-trigger',
      'relative isolate cursor-pointer rounded-md select-none',

      // User classes
      this.userClass(),
    ),
  );

  toggle() {
    this.collapsable.toggle();
  }
}

@Component({
  selector: 'collapsable-trigger',
  exportAs: 'collapsableTrigger',
  hostDirectives: [CollapsableTriggerBehavior],
  template: `<ng-content />`,
})
export class CollapsableTrigger {}

@Component({
  selector: 'collapsable-content',
  exportAs: 'collapsableContent',
  imports: [NgTemplateOutlet],
  host: {
    '[id]': 'contentId',
    '[class]': 'computedClass()',
  },
  template: `
    @if (expanded()) {
      <ng-container *ngTemplateOutlet="content" />
    }

    <ng-template #content>
      <ng-content />
    </ng-template>
  `,
})
export class CollapsableContent {
  // Dependencies
  private readonly collapsable = inject(Collapsable);

  // Inputs / Outputs
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  readonly expanded = computed(() => this.collapsable.expanded());
  protected readonly contentId = this.collapsable.contentId;
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsable-content',

      // User classes
      this.userClass(),
    ),
  );
}
